import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info, Loader2 } from 'lucide-react';

export interface OrgPickerOrg {
  id: string;
  name: string;
  connections?: unknown;
}

export interface MigratePickerOptions {
  force: boolean;
  dryRun: boolean;
  snapshotsOnly: boolean;
  snapshotFromDate?: string;
  snapshotToDate?: string;
  includedConnections: string[];
}

interface OrgPickerPanelProps {
  orgs: OrgPickerOrg[];
  loading: boolean;
  isSubmitting: boolean;
  migratedOrgIds?: Set<string>;
  onSubmit: (orgIds: string[], options: MigratePickerOptions) => Promise<boolean>;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function OptionInfo({ label, text }: { label: string; text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`About ${label}`}
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-gray-400 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

export function connectionIdsFromOrg(connections: unknown): string[] {
  if (Array.isArray(connections)) {
    return connections.flatMap(item => {
      if (!item || typeof item !== 'object') return [];
      const rec = item as Record<string, unknown>;
      const id = String(rec.connectionId ?? rec.id ?? '');
      return id ? [id] : [];
    });
  }
  if (connections && typeof connections === 'object') {
    return Object.entries(connections as Record<string, unknown>).map(([key, val]) => {
      if (val && typeof val === 'object') {
        const rec = val as Record<string, unknown>;
        return String(rec.connectionId ?? rec.id ?? key);
      }
      return key;
    }).filter(Boolean);
  }
  return [];
}

export function OrgPickerPanel({
  orgs, loading, isSubmitting, migratedOrgIds = new Set(), onSubmit,
}: OrgPickerPanelProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [force, setForce] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [snapshotsOnly, setSnapshotsOnly] = useState(false);
  const [snapshotFromDate, setSnapshotFromDate] = useState('');
  const [snapshotToDate, setSnapshotToDate] = useState('');
  const [includedConnections, setIncludedConnections] = useState<Set<string>>(new Set());

  const filtered = orgs.filter(o =>
    o.name.toLowerCase().includes(search.toLowerCase()),
  );

  const availableConnections = useMemo(() => {
    const ids = new Set<string>();
    orgs.forEach(org => {
      if (!selected.has(org.id)) return;
      connectionIdsFromOrg(org.connections).forEach(id => ids.add(id));
    });
    return [...ids].sort();
  }, [orgs, selected]);

  useEffect(() => {
    const allowed = new Set(availableConnections);
    setIncludedConnections(prev => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach(id => {
        if (allowed.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [availableConnections]);

  const toggleOrg = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleConnection = (id: string) => {
    setIncludedConnections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map(o => o.id)));
  const clearAll = () => {
    setSelected(new Set());
    setIncludedConnections(new Set());
  };

  const overLimit = selected.size > 50;
  const hasAnyMigrated = [...selected].some(id => migratedOrgIds.has(id));
  const dateInvalid = Boolean(
    (snapshotFromDate && !ISO_DATE_RE.test(snapshotFromDate))
    || (snapshotToDate && !ISO_DATE_RE.test(snapshotToDate))
    || (snapshotFromDate && snapshotToDate && snapshotFromDate > snapshotToDate),
  );
  const canSubmit = selected.size > 0 && !overLimit && !isSubmitting && !dateInvalid;

  const buttonLabel = overLimit ? 'Max 50 orgs per run' : 'Start migration';

  const resetForm = () => {
    setSelected(new Set());
    setForce(false);
    setDryRun(false);
    setSnapshotsOnly(false);
    setSnapshotFromDate('');
    setSnapshotToDate('');
    setIncludedConnections(new Set());
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Select Organisations</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          placeholder="Search organisations…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="flex gap-2">
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={selectAll}>
            Select all
          </Button>
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={clearAll}>
            Clear
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading organisations…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-500">No organisations found.</p>
        ) : (
          <ScrollArea className="h-72 border rounded-md p-2">
            {filtered.map(org => (
              <div key={org.id} className="flex items-center gap-2 py-1">
                <Checkbox
                  id={`org-${org.id}`}
                  checked={selected.has(org.id)}
                  onCheckedChange={() => toggleOrg(org.id)}
                />
                <Label htmlFor={`org-${org.id}`} className="flex-1 cursor-pointer text-sm">
                  {org.name}
                </Label>
              </div>
            ))}
          </ScrollArea>
        )}

        {overLimit && (
          <p className="text-sm text-red-600">
            Max 50 orgs per run — {selected.size} selected.
          </p>
        )}

        <TooltipProvider delayDuration={200}>
        <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="force-remigrate"
            checked={force}
            onCheckedChange={checked => setForce(checked === true)}
          />
          <Label htmlFor="force-remigrate" className="cursor-pointer text-sm">
            Force re-migrate
          </Label>
          <OptionInfo
            label="Force re-migrate"
            text="Overwrite this org on staging even if it was already migrated. Last resort — prefer Resume on a failed or stalled run. When a snapshot date filter is set, Force still drops other connection tables but keeps existing inventory/financial snapshot history outside the range."
          />
        </div>
        {hasAnyMigrated && !force && !snapshotsOnly && (
          <p className="text-sm text-amber-700">
            Selected organisation(s) already on staging will be skipped unless you check Force re-migrate or Snapshots only.
          </p>
        )}

        <div className="flex items-center gap-2">
          <Checkbox
            id="dry-run"
            checked={dryRun}
            onCheckedChange={checked => setDryRun(checked === true)}
          />
          <Label htmlFor="dry-run" className="cursor-pointer text-sm">
            Dry run
          </Label>
          <OptionInfo
            label="Dry run"
            text="Preview only. Logs every table and file that would be copied, but does not write anything to staging."
          />
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="snapshots-only"
            checked={snapshotsOnly}
            onCheckedChange={checked => setSnapshotsOnly(checked === true)}
          />
          <Label htmlFor="snapshots-only" className="cursor-pointer text-sm">
            Snapshots only
          </Label>
          <OptionInfo
            label="Snapshots only"
            text="Copy only inventory and financial snapshot tables. Skips the org record, app tables, connection databases, and PO files. Use when the org is already on staging."
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="snapshot-from" className="text-sm">Snapshot from</Label>
              <OptionInfo
                label="Snapshot from"
                text="Earliest snapshot date to copy, inclusive. Applies only to inventory and financial snapshot tables. Leave blank to include all dates from the start."
              />
            </div>
            <Input
              id="snapshot-from"
              type="date"
              value={snapshotFromDate}
              onChange={e => setSnapshotFromDate(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="snapshot-to" className="text-sm">Snapshot to</Label>
              <OptionInfo
                label="Snapshot to"
                text="Latest snapshot date to copy, inclusive. Applies only to inventory and financial snapshot tables. Leave blank to include all dates through the latest."
              />
            </div>
            <Input
              id="snapshot-to"
              type="date"
              value={snapshotToDate}
              onChange={e => setSnapshotToDate(e.target.value)}
            />
          </div>
        </div>
        {dateInvalid && (
          <p className="text-sm text-red-600">Snapshot from must be on or before snapshot to.</p>
        )}

        {!snapshotsOnly && availableConnections.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <p className="text-sm text-gray-700">Connections (empty = all)</p>
              <OptionInfo
                label="Connections"
                text="Optional. Leave all unchecked to copy every connection for the selected orgs. Checking IDs limits the run to those connection databases."
              />
            </div>
            <ScrollArea className="max-h-32 border rounded-md p-2">
              {availableConnections.map(id => (
                <div key={id} className="flex items-center gap-2 py-1">
                  <Checkbox
                    id={`conn-${id}`}
                    checked={includedConnections.has(id)}
                    onCheckedChange={() => toggleConnection(id)}
                  />
                  <Label htmlFor={`conn-${id}`} className="flex-1 cursor-pointer text-xs font-mono">
                    {id}
                  </Label>
                </div>
              ))}
            </ScrollArea>
          </div>
        )}
        </div>
        </TooltipProvider>

        <Button
          onClick={async () => {
            if (!canSubmit) return;
            const ok = await onSubmit(Array.from(selected), {
              force,
              dryRun,
              snapshotsOnly,
              snapshotFromDate: snapshotFromDate || undefined,
              snapshotToDate: snapshotToDate || undefined,
              includedConnections: snapshotsOnly ? [] : Array.from(includedConnections),
            });
            if (ok) resetForm();
          }}
          disabled={!canSubmit}
          className="w-full"
        >
          {isSubmitting ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Starting…</>
          ) : (
            buttonLabel
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
